import React from 'react';
import Icon from '../../../components/AppIcon';

const TermsOfServiceContent = ({ searchQuery }) => {
  // Helper function to highlight search terms
  const highlightSearchTerm = (text, searchQuery) => {
    if (!searchQuery?.trim()) return text;
    
    const regex = new RegExp(`(${searchQuery})`, 'gi');
    const parts = text?.split(regex);
    
    return parts?.map((part, index) => 
      regex?.test(part) ? (
        <mark key={index} className="bg-yellow-200 px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  const termsContent = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      icon: 'CheckCircle',
      content: `By accessing and using Intime's applicant tracking system, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. These terms constitute a legally binding agreement between you and Intime. If you do not agree to these terms, you may not use our services.

      These terms apply to all users of our platform, including but not limited to hiring managers, recruiters, HR professionals, and job applicants. By creating an account or using our services, you represent that you have the legal authority to enter into this agreement.`
    },
    {
      id: 'service-description',title: '2. Service Description',icon: 'Monitor',
      content: `Intime provides a cloud-based applicant tracking system designed to streamline the hiring process for organizations of all sizes. Our services include:

      • Job posting management and distribution
      • Candidate application tracking and management  
      • Resume parsing and candidate profile organization
      • Interview scheduling and feedback collection
      • Analytics and reporting on hiring metrics
      • Integration with third-party job boards and HR tools
      • Collaborative hiring workflows and team management

      We reserve the right to modify, update, or discontinue any aspect of our services at any time with appropriate notice to users.`
    },
    {
      id: 'user-accounts',title: '3. User Accounts and Registration',icon: 'UserPlus',content: `To use Intime's services, you must create an account and provide accurate, complete, and current information. You are responsible for:

      • Maintaining the confidentiality of your account credentials
      • All activities that occur under your account
      • Notifying us immediately of any unauthorized use
      • Ensuring your account information remains accurate and up-to-date

      You may not create multiple accounts, share your account with others, or use automated tools to create accounts. Organizations are responsible for managing their team members' access and ensuring compliance with these terms.`
    },
    {
      id: 'acceptable-use',title: '4. Acceptable Use Policy',icon: 'AlertTriangle',
      content: `You agree to use our services only for lawful purposes and in accordance with these terms. Prohibited uses include:

      • Violating any applicable laws or regulations
      • Infringing on intellectual property rights
      • Transmitting harmful, offensive, or inappropriate content
      • Attempting to gain unauthorized access to our systems
      • Using our services to discriminate illegally in hiring
      • Interfering with other users' access to our services
      • Reverse engineering or attempting to extract our source code

      Violations may result in immediate account suspension or termination.`
    },
    {
      id: 'payment-terms',
      title: '5. Payment Terms and Billing',
      icon: 'CreditCard',
      content: `Intime offers subscription plans with monthly billing cycles. Payment terms include:

      • Fees are charged in advance for each billing period
      • All payments are non-refundable except as required by law
      • Price changes will be communicated 30 days in advance
      • Failed payments may result in service suspension
      • You may cancel your subscription at any time
      • Refunds for annual subscriptions are prorated for unused time

      We use secure third-party payment processors and do not store your payment card information on our servers.`
    },
    {
      id: 'intellectual-property',
      title: '6. Intellectual Property Rights',
      icon: 'Copyright',
      content: `Intime and our licensors own all rights, title, and interest in our services, including:

      • Software, technology, and algorithms
      • Trademarks, logos, and brand elements  
      • Documentation and user interfaces
      • Proprietary methodologies and processes

      You retain ownership of content you upload, but grant us a license to use, store, and process it to provide our services. You represent that you have the right to upload and use all content in our system.

      We respect intellectual property rights and respond to valid DMCA takedown notices.`
    },
    {
      id: 'data-protection',
      title: '7. Data Protection and Privacy',
      icon: 'Lock',
      content: `Your privacy is important to us. Our data practices are governed by our Privacy Policy, which forms part of these terms. Key commitments include:

      • Processing personal data lawfully, fairly, and transparently
      • Implementing appropriate security measures
      • Respecting individual privacy rights under GDPR and CCPA
      • Limiting data use to providing and improving our services
      • Providing data portability and deletion upon request

      By using our services, you consent to our Privacy Policy and data processing practices as described therein.`
    },
    {
      id: 'service-availability',
      title: '8. Service Availability and Support',
      icon: 'Headphones',
      content: `While we strive to provide reliable service, we do not guarantee 100% uptime. Our service level commitments include:

      • 99.9% uptime target for our platform
      • Regular maintenance windows with advance notice
      • Email support for all paid plans
      • Priority support for enterprise customers
      • Service status updates during outages

      We are not liable for service interruptions caused by factors beyond our reasonable control, including internet connectivity issues, third-party service failures, or force majeure events.`
    },
    {
      id: 'limitation-liability',
      title: '9. Limitation of Liability',
      icon: 'ShieldAlert',
      content: `To the maximum extent permitted by law, Intime's liability is limited as follows:

      • Total liability shall not exceed the amount paid by you in the 12 months prior to the claim
      • We are not liable for indirect, incidental, or consequential damages
      • We do not warrant that our service will meet all your requirements
      • You use our services at your own risk
      • Some jurisdictions do not allow liability limitations, so these may not apply to you

      This limitation applies regardless of the theory of liability, whether based on warranty, contract, statute, tort, or otherwise.`
    },
    {
      id: 'termination',title: '10. Termination',icon: 'XCircle',
      content: `Either party may terminate this agreement:

      • You may cancel your account at any time through your account settings
      • We may terminate for material breach after 30 days' written notice
      • We may suspend service immediately for violations of acceptable use
      • Termination does not relieve you of payment obligations incurred before termination

      Upon termination:
      • Your access to services will cease
      • You may export your data within 30 days
      • We will delete your data according to our retention policy
      • Surviving terms include intellectual property, limitation of liability, and dispute resolution.`
    },
    {
      id: 'governing-law',
      title: '11. Governing Law and Disputes',
      icon: 'Gavel',
      content: `These terms are governed by the laws of [Jurisdiction], without regard to conflict of law principles. Dispute resolution process:

      • First, contact us to resolve the matter informally
      • If unresolved, disputes will be settled through binding arbitration
      • Arbitration will be conducted under [Arbitration Rules]
      • Class action lawsuits are waived
      • Small claims court remains available for individual claims

      You consent to the exclusive jurisdiction of courts in [Jurisdiction] for any matters not subject to arbitration.`
    },
    {
      id: 'changes-terms',
      title: '12. Changes to Terms',
      icon: 'Edit',
      content: `We may modify these terms from time to time. When we do:

      • We will provide at least 30 days' notice of material changes
      • Notice will be sent to your registered email address
      • Changes will be posted on our website with the effective date
      • Continued use after the effective date constitutes acceptance
      • If you don't agree to changes, you may terminate your account

      We encourage you to review these terms periodically to stay informed of any updates.`
    }
  ];

  return (
    <div className="space-y-12">
      {/* Terms of Service Introduction */}
      <section className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
        <div className="flex items-start">
          <Icon name="Scale" size={24} className="text-blue-600 mr-4 mt-1 flex-shrink-0" />
          <div>
            <h2 className="text-2xl font-bold text-blue-900 mb-3">
              Terms of Service Agreement
            </h2>
            <p className="text-blue-800 text-lg leading-relaxed">
              {highlightSearchTerm(
                "These Terms of Service govern your use of Intime's applicant tracking system. Please read them carefully as they establish the legal relationship between you and our company, define your rights and responsibilities, and outline the terms under which you may access and use our platform.",
                searchQuery
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Terms Content Sections */}
      {termsContent?.map((section) => (
        <section key={section?.id} id={section?.id} className="scroll-mt-24">
          <div className="flex items-start mb-6">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <Icon name={section?.icon} size={24} className="text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {highlightSearchTerm(section?.title, searchQuery)}
              </h2>
              <div className="prose prose-lg text-gray-700 leading-relaxed whitespace-pre-line">
                {highlightSearchTerm(section?.content, searchQuery)}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Contact for Terms Questions */}
      <section className="bg-gray-50 border border-gray-200 rounded-lg p-8">
        <div className="text-center">
          <Icon name="MessageCircle" size={32} className="text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            Questions About These Terms?
          </h3>
          <p className="text-gray-600 mb-4">
            If you have any questions about our Terms of Service, please don't hesitate to contact our legal team.
          </p>
          <a
            href="mailto:legal@intime.com?subject=Terms of Service Question"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Icon name="Mail" size={16} className="mr-2" />
            Contact Legal Team
          </a>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServiceContent;